// Business Process Improvement client (/api/bpm).
//
// Mirrors backend/apis/bpm_improvement.py. All step metrics and API figures are
// mock, deterministic demo data — the backend says so in `assumptions.note`.

import { get, post, del, API_BASE, ApiError, AI_TIMEOUT_MS } from "../../api/http";

/** Seeded sample processes plus demands that carry discovery BPMN. */
export const listProcesses = () => get("/bpm/processes");

/** BPMN XML, lanes and per-step mock metrics. */
export const getProcess = (id) => get(`/bpm/processes/${encodeURIComponent(id)}`);

/** Deterministic findings, KPIs, per-step costs and the system/API table. */
export const analyzeProcess = (id) => get(`/bpm/analyze/${encodeURIComponent(id)}`);

/** AI (or template fallback) improvement plan. */
export const getNarrative = (id, { refresh = false } = {}) =>
  post(`/bpm/analyze/${encodeURIComponent(id)}/narrative`, undefined,
       { query: { refresh: refresh || undefined }, timeout: AI_TIMEOUT_MS });

/* ------------------------------------------------------------ process import */
// backend/apis/bpm_import.py. Uploads are multipart, which the JSON client in
// api/http.js does not send, so this one call uses fetch directly but reports
// failures the same way (ApiError with the FastAPI `detail`).

/** Previously imported processes (newest first). */
export const listImports = () => get("/bpm/imports");

/** Remove an imported process. */
export const deleteImport = (id) => del(`/bpm/imports/${encodeURIComponent(id)}`);

/** URL that downloads a process (sample, demand or imported) as a .bpmn file. */
export const exportUrl = (id) => `${API_BASE}/bpm/export/${encodeURIComponent(id)}.bpmn`;

/** Formats the importer accepts (mirrors bpm_import.SUPPORTED). */
export const ACCEPT = ".bpmn,.bpmn2,.xml,.vsdx,.pdf,.png,.jpg,.jpeg";
export const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;
export const isAiFormat = (filename = "") => /\.(pdf|png|jpe?g)$/i.test(filename);

function currentUser() {
  try {
    const u = JSON.parse(localStorage.getItem("currentUser") || "null");
    return u?.email || u?.username || u?.name || "";
  } catch {
    return "";
  }
}

/** Upload a BPMN / Visio / PDF / image file. Resolves to the import summary. */
export async function importProcess(file, { name, signal } = {}) {
  const body = new FormData();
  body.append("file", file);
  if (name) body.append("name", name);
  const user = currentUser();
  if (user) body.append("created_by", user);

  const path = "/bpm/import";
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), AI_TIMEOUT_MS);
  if (signal) signal.addEventListener("abort", () => controller.abort(), { once: true });
  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, { method: "POST", body, signal: controller.signal });
  } catch (e) {
    throw new ApiError(e.name === "AbortError"
      ? `Import timed out after ${Math.round(AI_TIMEOUT_MS / 1000)}s`
      : `Import could not reach the server: ${e.message}`, { path, method: "POST" });
  } finally {
    clearTimeout(timer);
  }
  const text = await res.text().catch(() => "");
  let payload = null;
  try { payload = text ? JSON.parse(text) : null; } catch { payload = null; }
  if (!res.ok) {
    const d = payload?.detail;
    const msg = typeof d === "string" ? d
      : Array.isArray(d) ? d.map((x) => x.msg).join("; ")
      : `HTTP ${res.status}`;
    throw new ApiError(msg, { status: res.status, detail: d ?? text, path, method: "POST" });
  }
  return payload;
}
